import { useState } from "react";
import { useCreateCustomerGroup } from "../hooks/useCustomerGroups";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage, getFieldErrors } from "@/shared/utils/errorHandler";
import { useConfirm } from "@/shared/contexts/confirm-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel } from "@/components/ui/field";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { usePaymentTerms } from "@/shared/hooks/useMetaData";


const initialFormData = {
  name: "",
  description: "",
  defaultPaymentTerms: "COD",
  defaultCreditLimit: 0,
  defaultDiscountPercentage: 0,
  isDefault: false,
};

export const CreateGroupsDialog = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

  const notify = useNotification();
  const createMutation = useCreateCustomerGroup();
  const confirm = useConfirm();
  const paymentTermsQuery = usePaymentTerms();

  const handleChange = (
    field: keyof typeof initialFormData,
    value: string | number | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});

    confirm({
      title: "Create customer group?",
      description: `“${formData.name}” will be added as a new customer group.`,
      confirmLabel: "Create group",
      onConfirm: () =>
        createMutation
          .mutateAsync(formData)
          .then(() => {
            notify.success("Customer group created successfully");
            setFormData(initialFormData);
            setOpen(false);
          })
          .catch((error: unknown) => {
            const fe = getFieldErrors(error);
            if (fe) {
              setFieldErrors(fe);
            }
            notify.error(getErrorMessage(error));
            throw error;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="w-4 h-4" />
          New Group
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Customer Group</DialogTitle>
          <DialogDescription>
            Create a new customer group with default settings
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="name">
                  Group Name <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.name && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.name.join(", ")}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="isDefault">Default Group</FieldLabel>
                <div className="flex items-center space-x-2 mt-2">
                  <Checkbox
                    id="isDefault"
                    checked={formData.isDefault}
                    onCheckedChange={(checked) =>
                      handleChange("isDefault", checked)
                    }
                    disabled={createMutation.isPending}
                  />
                  <Label htmlFor="isDefault" className="cursor-pointer">
                    Set as default for new customers
                  </Label>
                </div>
              </Field>
            </div>

            <Field className="mt-4">
              <FieldLabel htmlFor="description">
                Description <span className="text-red-500">*</span>
              </FieldLabel>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                required
                disabled={createMutation.isPending}
                placeholder="Describe this customer group"
                className="flex min-h-20 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
              />
              {fieldErrors.description && (
                <div className="text-red-500 text-xs mt-1">
                  {fieldErrors.description.join(", ")}
                </div>
              )}
            </Field>
          </div>

          {/* Default Settings */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Default Settings</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
              <Field>
                <FieldLabel htmlFor="defaultPaymentTerms">
                  Payment Terms <span className="text-red-500">*</span>
                </FieldLabel>
                {paymentTermsQuery.isLoading ? (
                  <div className="h-10 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <Select
                    value={formData.defaultPaymentTerms}
                    onValueChange={(value) =>
                      handleChange('defaultPaymentTerms', value)
                    }
                    disabled={createMutation.isPending}
                  >
                    <SelectTrigger id="defaultPaymentTerms" className="w-full">
                      <SelectValue placeholder="Select Payment Terms" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentTermsQuery.data?.map((term) => (
                        <SelectItem key={term.value} value={term.value}>
                          {term.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {fieldErrors.defaultPaymentTerms && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.defaultPaymentTerms.join(', ')}
                  </div>
                )}
              </Field>

              <Field>
                <FieldLabel htmlFor="defaultCreditLimit">
                  Credit Limit <span className="text-red-500">*</span>
                </FieldLabel>
                <Input
                  id="defaultCreditLimit"
                  type="number"
                  value={formData.defaultCreditLimit}
                  onChange={(e) =>
                    handleChange('defaultCreditLimit', parseFloat(e.target.value) || 0)
                  }
                  required
                  disabled={createMutation.isPending}
                />
                {fieldErrors.defaultCreditLimit && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.defaultCreditLimit.join(', ')}
                  </div>
                )}
              </Field>

              <Field className="col-span-2">
                <FieldLabel htmlFor="defaultDiscountPercentage">
                  Default Discount Percentage <span className="text-red-500">*</span>
                </FieldLabel>
                <div className="flex items-center gap-2">
                  <Input
                    id="defaultDiscountPercentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={formData.defaultDiscountPercentage}
                    onChange={(e) =>
                      handleChange('defaultDiscountPercentage', parseFloat(e.target.value) || 0)
                    }
                    required
                    disabled={createMutation.isPending}
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
                {fieldErrors.defaultDiscountPercentage && (
                  <div className="text-red-500 text-xs mt-1">
                    {fieldErrors.defaultDiscountPercentage.join(', ')}
                  </div>
                )}
              </Field>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={createMutation.isPending}
          >
            {createMutation.isPending ? "Creating..." : "Create Group"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
